import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import getAccessToken from '@r1-frontend/shared/helpers/getAccessToken';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { IShopItem } from '~/src/api/content/shop/getShopItems';
import AdaptiveNukaCarousel from '~/src/components/AdaptiveNukaCarousel';
import OrderPopup from '~/src/components/Equipment/internet/orderPopup';
import PopupResult from '~/src/components/Popups/PopupResult';
import ProductCard from '~/src/components/ProductCard';
import { getPriceOptions } from '~/src/entities/equipment';
import { convertShopPrices } from '~/src/entities/price';
import { EEquipmentType } from '~/src/features/domofon/purchase/types/EEquipmentType';
import { useScroll } from '~/src/helpers/hooks/useScroll';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

interface IProps {
    products: IShopItem[],
}

interface IResultPopup {
    title: string,
    text: string,
    type: EResultPopupStatus,
}

interface ISelectedOption {
    id: number,
    label: string,
    operType: string,
    price: number,
    pricePromo: number,
    sort: null,
    type: number | string,
    value: number | string,
}

export enum EMediaWidth {
    mobile = '(max-width: 767px)',
}

const ProductsBlock: React.FC<IProps> = ({ products }): JSX.Element => {
    const csrf = useSelector(selectCsrf);
    const isMobile = useMatchMedia(EMediaWidth.mobile);
    const scrollToProducts = useScroll({ alignment: 'start' }, -80);
    const [orderRequestResult, setOrderRequestResult] = useState<IResultPopup>({
        title: '',
        text: '',
        type: EResultPopupStatus.DEFAULT,
    });
    const [name, setName] = useState('');
    const [label, setLabel] = useState('');
    const [selectedOption, setSelectedOption] = useState<ISelectedOption | null>(null);
    const [isOrderPopupOpened, setIsOrderPopupOpened] = useState(false);
    const [isOrderResult, setIsOrderResult] = useState(false);
    const productsRef = useRef<HTMLDivElement>(null);

    const onDetail = useCallback(({ selectedOption, name }: { selectedOption: ISelectedOption, name: string }) => {
        setName(name);
        setLabel(`${name} | ${selectedOption.operType} | ${selectedOption.price}`);
        setIsOrderPopupOpened(true);
        setSelectedOption(selectedOption);
    }, []);

    useEffect(() => {
        if (location.hash === '#equipment') {
            scrollToProducts(productsRef);
        }
    }, [scrollToProducts]);

    const onSuccessOrder = useCallback((message: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: ACTIONS.finished,
            label: `popup | ${name} | ${selectedOption?.price} | ${selectedOption?.operType}`,
        });

        setOrderRequestResult(prevState => ({
            ...prevState,
            type: EResultPopupStatus.SUCCESS,
            title: 'Заявка на подключение отправлена',
            text: message,
        }));
    }, [name, selectedOption?.price, selectedOption?.operType]);


    const onErrorOrder = useCallback((message: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: ACTIONS.error,
            label: `popup | ${name} | ${selectedOption?.price} | ${selectedOption?.operType}`,
        });

        setOrderRequestResult(prevState => ({
            ...prevState,
            type: EResultPopupStatus.ERROR,
            title: 'Произошла ошибка',
            text: message,
        }));
    }, [selectedOption?.price, selectedOption?.operType, name]);

    const popupRequestHandler = useCallback(async({ fio, phone }: { fio: string, phone: string}) => {
        const dataParams: RequestDto = {
            fio,
            phone,
            product_id: [EProductsId.domofon],
            equip_price_id: selectedOption?.id,
            csrf,
        };

        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection(dataParams),
        );
        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            if (ok) {
                onSuccessOrder(message);
            } else {
                onErrorOrder(message);
            }
        } else {
            const message = resp.error.message || 'Возникла системная ошибка. Пожалуйста, попробуйте еще раз.';
            onErrorOrder(message);
        }

        setIsOrderPopupOpened(false);
        setIsOrderResult(true);
    }, [onErrorOrder, onSuccessOrder, selectedOption?.id]);

    const closeOrderPopup = () => {
        setSelectedOption(null);
        setName('');
        setIsOrderPopupOpened(false);
    };

    const renderProductCards = useCallback(() => {
        return products.map(product => {
            const links: { buttonLink?: string, linkUrl?: string } = {
                buttonLink: product.button_link,
            };

            if (product.alias === EEquipmentType.intercomKey && getAccessToken() !== null) {
                delete links.buttonLink;
                links.linkUrl = !product.checkProductId && product.showForDelivery && product.leftovers !== null && product.leftovers > 0
                    ? `/lk/purchase/intercom/${product.alias}`
                    : undefined;
            }

            return (
                // @ts-ignore потому что ProductCard.JS
                <ProductCard
                    key={product.id}
                    image={product.image}
                    imageWebp={product.image_webp}
                    categoryTitle={product?.category?.title || ''}
                    categoryName={product?.category?.name || ''}
                    body={product.body_full}
                    name={product.title}
                    shortName={product.title_short}
                    description={product.body_full}
                    spec={product.props}
                    priceOptions={getPriceOptions(convertShopPrices(product))}
                    detailHandler={onDetail}
                    labels={product.labels}
                    {...links}
                />
            );
        });
    }, [products, onDetail]);

    return (
        <ST.Wrapper data-test='products-block' ref={productsRef}>
            <WideContainer>
                <ST.Desc>
                    <ST.HeadTitle data-test='products-block-title'>Вы можете сделать домофон ещё удобнее</ST.HeadTitle>
                    <ST.SubHeadTitle data-test='products-block-title'>С дополнительным оборудованием от Дом.ру </ST.SubHeadTitle>
                </ST.Desc>
                {isClientSide()
                    ? (<AdaptiveNukaCarousel
                        desktopSlideWidth={'304px'}
                        mobileSlideWidth={'304px'}
                        withoutDots={!isMobile}
                        framePadding={!isMobile ? '-24px 0 0 0' : '-24px 0 0 16px'}
                        cellSpacing={16}
                        slidesToScroll={'auto'}
                        heightMode='max'
                    >
                        {renderProductCards()}
                    </AdaptiveNukaCarousel>)
                    : (<ST.NoSsrContainer>
                        {renderProductCards()}
                    </ST.NoSsrContainer>)}
            </WideContainer>
            <OrderPopup
                isOpened={isOrderPopupOpened}
                closeClick={closeOrderPopup}
                sendRequest={popupRequestHandler}
                name={name}
                isPopup={true}
                selectedOption={selectedOption}
                extLabel={label}
            />
            <PopupResult
                isOpen={isOrderResult}
                onClose={() => setIsOrderResult(false)}
                {...orderRequestResult}
            />
        </ST.Wrapper>
    );
};

export default ProductsBlock;
